import { useCallback, useEffect, useMemo, useState } from "react";
import { CookiesProvider } from "react-cookie";
import { defaultFallbackInView } from "react-intersection-observer";
import { MittProvider } from "react-mitt";

import nextI18NextConfig from "../next-i18next.config.js";
import { appWithTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import Script from "next/script";

import { AuthProvider, useAuth } from "auth";
import MainLayout from "components/Layouts/MainLayout";
import Canonical from "components/common/Canonical";
import HrefLang from "components/common/HrefLang";
import useLogMissingKeys from "hooks/useLogMissingKeys";
import useSmoothScrollOnNavigation from "hooks/useSmoothScrollOnNavigation";

import { useApollo } from "../lib/apollo/client";
import useRegisterModals from "../src/hooks/useRegisterModals";
import "../src/index.css";
import { useUnreadMessagesCount } from "@/Apollo/hooks/chat/queries/useUnreadMessagesCount";
import { IconSprite } from "@/assets/icons/sprite/IconSprite";
import { NoIndex } from "@/common/components/no-index";
import { usePageTranslations } from "@/common/hooks/i18n/usePageTranslations";
import { useMerchantTOS } from "@/common/hooks/merchant/useMerchantTOS";
import { clearLegacyCookieConsentPopupCookieValueIfExists } from "@/common/utils/cookie.utils";
import { AppBanner } from "@/components/app-banner/AppBanner";
import { SmartAppBanner } from "@/components/app-banner/SmartAppBanner";
import { VacationModeBanner } from "@/components/vacation-mode/VacationModeBanner";
import { GTM_ID } from "@/config";
import { DisplayCurrencySync } from "@/features/display-currency/DisplayCurrencySync";
import { PageDisplayCurrencyProvider } from "@/features/display-currency/PageDisplayCurrency";
import { useExtensionInjectionDetection } from "@/features/extension-injection/useExtensionInjectionDetection";
import { ProWorkspaceProvider } from "@/features/pro-buyer-workspaces/components/ProWorkspaceProvider";
import { PromptwatchScript } from "@/features/promptwatch/components/PromptwatchScript";
import { makeQueryClient } from "@/features/search/api/query-client";
import { unpackDehydratedSearchState } from "@/features/search/utils/hydration.utils";
import { ExperimentsProvider } from "@/features/statsig/ExperimentsProvider";
import { STATSIG_STATE_PROP_NAME } from "@/features/statsig/experiment.static";
import {
  isFirstPartyWebVitalsEnabled,
  pushWebVitalToDataLayer,
} from "@/features/web-vitals/reportWebVitalsToDataLayer";
import useTrackPageView from "@/hooks/useTrackPageView";
import { ApolloProvider } from "@apollo/client";
import NiceModal from "@ebay/nice-modal-react";
import { HydrationBoundary, QueryClientProvider } from "@tanstack/react-query";
import { filsonPro, notoSans, poppins, roboto } from "@whoppah/fonts/index";

// https://github.com/thebuilder/react-intersection-observer#unsupported-fallback
defaultFallbackInView(true);

clearLegacyCookieConsentPopupCookieValueIfExists();

const Toaster = dynamic(() => import("components/common/Toaster"), {
  ssr: false,
});

const getDefaultLayout = (page, props) => {
  const { hideSearch, hideFooter } = props;
  return (
    <MainLayout hideSearch={hideSearch} hideFooter={hideFooter}>
      {page}
    </MainLayout>
  );
};

const Redirect = ({ to, passRedirectUrl }) => {
  const router = useRouter();

  const getActionFromUrl = useCallback(() => {
    const URL_TO_ACTION_MAP = {
      "/create-ad": "create-ad",
      "/account/favorites": "favorite",
      "/account/searches": "saved-search",
    };
    return URL_TO_ACTION_MAP[router.asPath];
  }, [router.asPath]);

  useEffect(() => {
    let pathname = to;
    const action = getActionFromUrl(router.asPath);
    let query = passRedirectUrl
      ? action
        ? { returnUrl: router.asPath, action, referrer: action }
        : { returnUrl: router.asPath }
      : undefined;

    /**
     * If `to` includes "?" we need to split it into `pathname` and `query`, otherwise
     * passing it as `pathname` only will result in incorrect URI encoding of the value.
     */
    if (pathname.includes("?")) {
      const components = pathname.split("?");
      pathname = components[0];
      query = components[1];
    }

    router.replace({ pathname, query });
  }, [router, to, getActionFromUrl, passRedirectUrl]);

  return null;
};

const InnerApp = ({ Component, pageProps }) => {
  const { isLoggedIn, isReady } = useAuth();
  const router = useRouter();

  useLogMissingKeys();
  useSmoothScrollOnNavigation();

  const appWithLayout = useMemo(() => {
    const getLayout = Component.getLayout || getDefaultLayout;
    const app = (
      <>
        {pageProps?.auth === "user" && <NoIndex />}
        <Component {...pageProps} />
        <VacationModeBanner />
      </>
    );
    return getLayout(app, pageProps);
  }, [Component, pageProps]);

  // Don't render auth protected pages if auth isn't ready
  if (!isReady && pageProps?.auth === "user") {
    return <NoIndex />;
  }

  // Redirect to home for guest pages
  if (isLoggedIn && pageProps?.auth === "guest") {
    return <Redirect to={router?.query?.returnUrl ?? "/"} />;
  }

  // Redirect to login for protected pages
  if (!isLoggedIn && pageProps?.auth === "user") {
    return <Redirect to="/account/login" passRedirectUrl />;
  }

  return appWithLayout;
};

// The useTrack[...] hooks are here because they use values from
// providers
const MyApp = props => {
  // First, so the page's translations are in the store before anything below
  // renders a `t()` (WHO-6644).
  usePageTranslations(props.pageProps?._nextI18Next);
  useTrackPageView();
  useUnreadMessagesCount();
  useRegisterModals();
  useMerchantTOS();
  useExtensionInjectionDetection();

  return (
    <>
      <style jsx global>{`
        :root {
          --font-poppins: ${poppins.style.fontFamily};
          --font-roboto: ${roboto.style.fontFamily};
          --font-noto-sans: ${notoSans.style.fontFamily};
          --font-filson-pro: ${filsonPro.style.fontFamily};
        }
      `}</style>
      <SmartAppBanner />
      {!props.pageProps?.hreflangUrls ? (
        <>
          <HrefLang />
          <Canonical />
        </>
      ) : null}
      {/* WHO-6629: Pro Buyer workspaces; renders nothing and does nothing outside its gate. */}
      <ProWorkspaceProvider>
        <NiceModal.Provider>
          <InnerApp {...props} />
        </NiceModal.Provider>
      </ProWorkspaceProvider>
      <AppBanner />
      <Toaster position="bottom-right" />
    </>
  );
};

// The app is translated here so that providers are not remounted when
// language is changed
const TranslatedApp = appWithTranslation(MyApp, nextI18NextConfig);

const Wrapper = props => {
  const client = useApollo(props.pageProps);
  const [queryClient] = useState(makeQueryClient);
  /* WHO-5547: the PLP ships its search state without default-valued fields; restore them. */
  const dehydratedState = props.pageProps?.dehydratedState;
  const hydrationState = useMemo(
    () => unpackDehydratedSearchState(dehydratedState),
    [dehydratedState]
  );

  return (
    <>
      {/* Symbol definitions for the sprite-backed icons; every page, once. */}
      <IconSprite />
      <ApolloProvider client={client}>
        <QueryClientProvider client={queryClient}>
          <HydrationBoundary state={hydrationState}>
            <CookiesProvider defaultSetOptions={{ path: "/" }}>
              <AuthProvider>
                <MittProvider>
                  <ExperimentsProvider
                    values={props.pageProps?.[STATSIG_STATE_PROP_NAME]}
                  >
                    <PageDisplayCurrencyProvider
                      value={props.pageProps?.displayCurrency}
                    >
                      <DisplayCurrencySync />
                      <TranslatedApp {...props} />
                    </PageDisplayCurrencyProvider>
                  </ExperimentsProvider>
                </MittProvider>
              </AuthProvider>
            </CookiesProvider>
          </HydrationBoundary>
        </QueryClientProvider>
      </ApolloProvider>
      <PromptwatchScript />
      {GTM_ID ? (
        <Script
          id="gtm"
          strategy="lazyOnload"
          src={`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`}
        />
      ) : null}
    </>
  );
};

export default Wrapper;

/*
  WHO-5479 S4. Behind NEXT_PUBLIC_FIRST_PARTY_WEB_VITALS this pushes the same
  `coreWebVitals` dataLayer event the GTM `web-vitals-ga4` tag produces, so
  that tag and its unpkg download can be paused. With the flag off (master's
  default) this function does nothing, which matches the previous line: the
  `reportWebVitals` re-exported from next-axiom 1.x does not exist there, so
  Next never had a hook to call and Axiom never received web vitals from it.
*/
export const reportWebVitals = metric => {
  if (isFirstPartyWebVitalsEnabled()) {
    pushWebVitalToDataLayer(metric);
  }
};
